import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { managementApi } from '#services/user';
import {
  ALL_TIME_RANGE_PRESET,
  TimeRangeValue,
  buildTimeRange,
} from '#components/time-range.utils';
import { revertAll } from '../actions';

interface UserListState {
  filter: string;
  dateRange: TimeRangeValue;
  filterKey: string;
  headerFilters: Record<string, Record<string, boolean>>;
  order: string;
  orderBy: string;
  pageIndex: number;
  rows: unknown[];
  rowCount: number;
  pageSize: number;
}

const initialState: UserListState = {
  filter: '',
  dateRange: buildTimeRange(ALL_TIME_RANGE_PRESET),
  filterKey: 'username',
  headerFilters: {},
  order: 'desc',
  orderBy: 'lastLoginAt',
  pageIndex: 0,
  rows: [],
  rowCount: 0,
  pageSize: 30,
};

const handleGetUsersFulfilled = (state: UserListState, action: { payload: unknown }) => {
  const { page, rows, total } = action.payload as {
    page: number;
    rows: unknown[];
    total: number;
  };
  state.pageIndex = page;
  state.rows = rows;
  state.rowCount = total;
};

export const userListSlice = createSlice({
  name: 'userList',
  initialState,
  reducers: {
    setDateRange(state, action: PayloadAction<TimeRangeValue>) {
      state.dateRange = action.payload;
      state.pageIndex = 0;
    },
    setFilter(state, action: PayloadAction<string>) {
      state.filter = action.payload;
    },
    setFilterKey(state, action: PayloadAction<string>) {
      state.filterKey = action.payload;
    },
    setHeaderFilters(state, action: PayloadAction<Record<string, Record<string, boolean>>>) {
      state.headerFilters = action.payload;
    },
    setOrder(state, action: PayloadAction<string>) {
      state.order = action.payload;
    },
    setOrderBy(state, action: PayloadAction<string>) {
      state.orderBy = action.payload;
    },
    setPageIndex(state, action: PayloadAction<number>) {
      state.pageIndex = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.pageIndex = 0;
    },
    setRows(state, action: PayloadAction<unknown[]>) {
      state.rows = action.payload;
    },
    setRowCount(state, action: PayloadAction<number>) {
      state.rowCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(revertAll, () => initialState)
      .addMatcher(managementApi.endpoints.getUsers.matchFulfilled, handleGetUsersFulfilled)
      .addMatcher(managementApi.endpoints.deleteUser.matchFulfilled, handleGetUsersFulfilled);
  },
});

export const {
  setDateRange,
  setFilter,
  setFilterKey,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setRows,
  setRowCount,
} = userListSlice.actions;

export default userListSlice.reducer;
